import type { ApiError as ApiErrorBody } from '@/types/api';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) || '/api/v1';

// Clerk JWT getter is set by the auth bootstrap (see hooks/useAuthBootstrap.ts).
// API client is framework-agnostic so it stays usable from non-React contexts (SSE consumer, etc).
let _getToken: (() => Promise<string | null>) | null = null;

export function setTokenGetter(fn: (() => Promise<string | null>) | null) {
  _getToken = fn;
}

/**
 * getClerkToken — read the currently wired Clerk JWT getter.
 * Returns null when not yet wired or when sign-in is missing. Non-React
 * consumers (SSE refresh path in useSessionSSE) need this without coupling
 * to the React auth context.
 */
export async function getClerkToken(): Promise<string | null> {
  if (!_getToken) return null;
  try {
    return await _getToken();
  } catch {
    return null;
  }
}

export class ApiError extends Error {
  /** trace_id from response X-Trace-ID header or envelope, when surfaced. */
  public traceId?: string;
  /** i18n key from envelope.message.i18n_key, when surfaced (legacy/M0 envelope). */
  public i18nKey?: string;
  /**
   * ★ M2 FIX-C2 (FE_SPEC §13.5/§13.6): canonical alias of `errorCode` so M2
   * call-sites can pattern-match on `e.code` (per spec snippet
   *   `catch (e: ApiError) { switch (e.code) { ... } }`).
   * Read from FLAT envelope `body.error_code` (NOT nested `body.error.code`).
   * Anchor PRD §11 + BE_SPEC §8.3.
   */
  public code: string;
  constructor(
    public status: number,
    public errorCode: string,
    message: string,
    public details?: ApiErrorBody,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = errorCode;
  }
}

interface ApiOpts extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Skip Authorization header (e.g. public health/version endpoints). */
  unauthenticated?: boolean;
  /**
   * Auto-generate Idempotency-Key (UUID v4) if not already present in headers.
   * Apply on POST/DELETE for the 6 mutating routes per auth_middleware.md §6.
   */
  idempotent?: boolean;
  /**
   * Return the raw Response instead of parsing JSON. Used for blob/markdown
   * downloads (reportDownloadMd) where the auth path must still inject JWT.
   */
  raw?: boolean;
}

/** uuidV4 — RFC 4122 v4 random UUID using crypto.randomUUID when available. */
export function uuidV4(): string {
  const c = (globalThis as any).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // Fallback: random hex (sufficient for client-only Idempotency-Key entropy).
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const h = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export async function api<T = unknown>(path: string, opts: ApiOpts = {}): Promise<T> {
  const { body, headers, unauthenticated, idempotent, raw, ...rest } = opts;

  const finalHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(headers as Record<string, string> | undefined),
  };

  if (idempotent && !finalHeaders['Idempotency-Key']) {
    finalHeaders['Idempotency-Key'] = uuidV4();
  }

  if (!unauthenticated && _getToken) {
    try {
      const token = await _getToken();
      if (token) finalHeaders['Authorization'] = `Bearer ${token}`;
    } catch {
      // Token fetch failure is non-fatal; backend will reject with 401 if required.
    }
  }

  const r = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...rest,
    headers: finalHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!r.ok) {
    let errBody: ApiErrorBody | undefined;
    try {
      errBody = (await r.json()) as ApiErrorBody;
    } catch {
      errBody = { error: 'unknown', message: r.statusText };
    }
    const m0Body = errBody as Record<string, unknown>;
    const m0Message = m0Body.message as
      | string
      | { i18n_key?: string; default_text_zh?: string; default_text_en?: string }
      | undefined;
    // ★ M2 FIX-C2 priority order: prefer FLAT envelope `error_code` (canonical
    // M2 shape per BE_SPEC §8.3) → legacy M0 `code` → legacy `error` field.
    const code = typeof m0Body.error_code === 'string'
      ? m0Body.error_code
      : typeof m0Body.code === 'string'
        ? m0Body.code
        : (typeof m0Body.error === 'string' ? m0Body.error : errBody?.error || 'unknown');
    const message = typeof m0Message === 'object' && m0Message !== null
      ? m0Message.default_text_zh || m0Message.default_text_en || r.statusText
      : (typeof m0Message === 'string' ? m0Message : errBody?.message || r.statusText);
    const err = new ApiError(r.status, code, message, errBody);
    err.traceId = r.headers.get('X-Trace-ID') ||
      (typeof m0Body.trace_id === 'string' ? m0Body.trace_id : undefined);
    if (typeof m0Message === 'object' && m0Message !== null && m0Message.i18n_key) {
      err.i18nKey = m0Message.i18n_key;
    }
    throw err;
  }

  if (raw) return r as unknown as T;
  if (r.status === 204) return undefined as T;
  const ct = r.headers.get('content-type') || '';
  if (ct.includes('application/json')) return (await r.json()) as T;
  return (await r.text()) as unknown as T;
}

export const get = <T = unknown>(path: string, init?: ApiOpts) =>
  api<T>(path, { ...init, method: 'GET' });
export const post = <T = unknown>(path: string, body?: unknown, init?: ApiOpts) =>
  api<T>(path, { ...init, method: 'POST', body });
export const patch = <T = unknown>(path: string, body?: unknown, init?: ApiOpts) =>
  api<T>(path, { ...init, method: 'PATCH', body });
export const del = <T = unknown>(path: string, init?: ApiOpts) =>
  api<T>(path, { ...init, method: 'DELETE' });
